import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  input,
  linkedSignal,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { MatMenu, MatMenuTrigger } from "@angular/material/menu";
import { FormArray, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { startWith, tap } from "rxjs/operators";
import { combineLatest, debounceTime } from "rxjs";
import { mergeWith } from "lodash";
import {
  BmExpGroupFg,
  BmItemForm,
  BmMgmFeeFg,
  BmMgmFeeGroupForm,
  BmMgmFeePcntForm,
  BudgetModelFormGroup,
} from "../../../pojo/bm-form";
import { BmMgmGeneralCalcService } from "../../../services/mgmFee/general/bm-mgm-general-calc.service";
import { SwitcherDashBracesPipe } from "../../../../../../util/pipes/switcherDashBraces.pipe";
import { ButtonComponent } from "../../../../../shared/component/buttons/button.component";
import { TableGroupIconComponent } from "../../../../../shared/component/table-group-icon/table-group-icon.component";
import { OnHoverDirective } from "../../../../../../util/directives/on-hover-directive";
import { InputComponent } from "../../../../../shared/component/inputs/input-component/input.component";
import { BmGrandTotalMgmFeeCalcService } from "../../../services/mgmFee/total/bm-grand-total-mgm-fee-calc.service";
import { GroupActionHelperService } from "../../service/group-action-helper.service";
import { BmDefaultFormCreatorService } from "../../../services/general/bm-default-form-creator.service";
import { BmSubscriptionsService } from "../../../services/general/subscription/bm-subscriptions.service";
import { BmCalcHelperService } from "../../../services/general/bm-calc-helper.service";
import { BmMgmFeeGroup, BmPeriodEnum } from "../../../pojo/bm-classes";
import { BmCalcType, BmGroupAction, BmGroupType, BmDataFillingType } from "../../../pojo/bm-enums";
import { CheckboxComponent } from "../../../../../shared/component/checkbox/checkbox.component";
import { DropDownItemComponent } from "../../../../../shared/component/drop-down-item/drop-down-item.component";
import { ThreeDotsMenuComponent } from "../../../../../shared/component/three-dots-menu/three-dots-menu.component";

@Component({
  selector: '[app-table-budget-model-body-mgm]',
  imports: [
    ButtonComponent,
    CheckboxComponent,
    DropDownItemComponent,
    InputComponent,
    MatMenu,
    OnHoverDirective,
    SwitcherDashBracesPipe,
    TableGroupIconComponent,
    ThreeDotsMenuComponent,
    ReactiveFormsModule,
    MatMenuTrigger,
    SwitcherDashBracesPipe,
    ButtonComponent,
    TableGroupIconComponent,
    OnHoverDirective,
    InputComponent,
  ],
  templateUrl: './table-budget-model-body-mgm.component.html',
  styleUrl: '../../table-budget-model.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableBudgetModelBodyMgmComponent implements OnChanges {
  protected readonly BmPeriodEnum = BmPeriodEnum;
  protected readonly BmGroupType = BmGroupType;
  protected readonly BmGroupAction = BmGroupAction;

  budgetForm = input.required<BudgetModelFormGroup>();
  collapsedYears = input.required<Map<number, boolean>>();
  isEditState = input.required<boolean>();

  budgetFormValue$$ = linkedSignal(() => {
    return this.budgetForm().getRawValue();
  });

  destroyRef = inject(DestroyRef);
  cdr = inject(ChangeDetectorRef);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.budgetForm) {
      this.subscribeOnFormChanges();
    }
  }

  subscribeOnFormChanges(): void {
    combineLatest([
      this.budgetForm().controls.yearsNumber.valueChanges.pipe(
        startWith(-1),
        takeUntilDestroyed(this.destroyRef)),
      this.budgetForm().controls.isAllQuartersCollapsed.valueChanges.pipe(
        startWith(null),
        takeUntilDestroyed(this.destroyRef)),
      this.budgetForm().controls.modelData.controls.mgmFee.valueChanges.pipe(
        startWith(null),
        takeUntilDestroyed(this.destroyRef)),
    ]).pipe(
      debounceTime(5), // form updates multiple times. We want to asap but leave an interval for batch updates.
      tap(([yearsNumber]) => {
        const isCopyEnabled = yearsNumber === -1 || yearsNumber === this.budgetFormValue$$().yearsNumber;
        this.updateLocalFormValue(isCopyEnabled);
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  /**
   * MANAGEMENT FEE
   *
   * Handle actions ADD, DELETE, DUPLICATE, RENAME in separate service
   * @param action type of action
   * @param groupFG selected FormGroup
   */
  onMgmFeeGroupAction(action: BmGroupAction, groupFG?: FormGroup<BmMgmFeeGroupForm>): void {
    const groupsFA = this.budgetForm().controls.modelData.controls.mgmFee.controls.groups;
    const mgmFee = groupsFA.parent as FormGroup<BmMgmFeeFg>;

    GroupActionHelperService.handleGroupAction(
      action,
      groupsFA,
      groupFG,
      (name: string, priority: number): BmMgmFeeGroup =>
        BmDefaultFormCreatorService.createDefaultMgmFeeGroup(name, this.budgetForm().controls.yearsNumber.value, priority),
      (group: BmMgmFeeGroup): FormGroup<BmMgmFeeGroupForm> =>
        BmMgmGeneralCalcService.createMgmFeeGroupForm(group, this.budgetForm().controls.yearsNumber.value),
      (fg) => BmSubscriptionsService.subscribeToMgmFeeGroup(fg),
      'Management fee group',
      true,
    );
    BmGrandTotalMgmFeeCalcService.recalculateGrandTotal(mgmFee);
    this.updateLocalFormValue(false);
  }

  /**
   * Handle edit item action (clicking on edit.svg in table sell)
   *
   * (EDIT)   Editable value was DISABLED => Make ENABLED and with calcType MANUAL
   * (CANCEL) Editable value was ENABLE => Make DISABLE and with calcType GENERATED
   *
   * @param item selected control
   * @param groupType needed to specify the group with unique logic
   */
  editItemValue(item: FormGroup<BmItemForm>, groupType?: BmGroupType): void {
    const itemsFa = item.parent as FormArray<FormGroup<BmItemForm>>;

    if (item.controls.itemValue.disabled) {
      item.controls.itemValue.enable({ emitEvent: false });
      item.controls.calcType.patchValue(BmCalcType.MANUAL, { emitEvent: false });
    } else {
      item.controls.itemValue.disable({ emitEvent: false });
      let previousValue: number;
      const formGroup = itemsFa.parent as FormGroup<any>; // could be various FormGroups
      const standardValue = formGroup.value.standardValue as number;

      switch (groupType) {
        case BmGroupType.MGM_FEE_PCNT:
          const formGroupMgmFee = itemsFa.parent as FormGroup<BmMgmFeePcntForm>;
          const standardValueMgmFee = formGroupMgmFee.value.standardValue;

          if (item.getRawValue().period.type === BmPeriodEnum.YEAR) {
            previousValue = standardValueMgmFee;
          }
          if (item.getRawValue().period.type === BmPeriodEnum.QUARTER) {
            previousValue = standardValueMgmFee / 4;
          }
          break;
        case BmGroupType.EXPENSES:
          previousValue = this.handleExpensesItemChange(itemsFa, item);
          break;
        case BmGroupType.INVESTMENT_VALUE:
          previousValue = 0;
          break;
        case BmGroupType.INVESTMENT_PCNT:
          previousValue = 0;
          break;
        default:
          previousValue = standardValue;
          break;
      }
      item.controls.calcType.patchValue(BmCalcType.GENERATED, { emitEvent: false });
      item.controls.itemValue.markAsDirty(); // mark as dirty = mark as changed by user in UI => needed to update values in service
      item.controls.itemValue.patchValue(previousValue);
    }
  }


  /**
   * Helper method to handle previous value calculation on 'x' icon click in Expenses group
   * Based on dataFillingType and standardValueExpenses will be previous value selected
   * @param itemsFa array of group Items
   * @param item selected Item
   */
  private handleExpensesItemChange(itemsFa: FormArray<FormGroup<BmItemForm>>, item: FormGroup<BmItemForm>): number {
    const formGroupExpenses = itemsFa.parent as FormGroup<BmExpGroupFg>;
    const standardValueExpenses = formGroupExpenses.value.standardValue;
    const isYearItem = item.getRawValue().period.type === BmPeriodEnum.YEAR;
    let previousValue: number;

    switch (formGroupExpenses.value.dataFillingType) {
      case BmDataFillingType.YEARLY:
        previousValue = isYearItem ? standardValueExpenses : (standardValueExpenses / 4);
        break;
      case BmDataFillingType.QUARTERLY:
        previousValue = isYearItem ? (standardValueExpenses * 4) : standardValueExpenses;
        break;
      case BmDataFillingType.MONTHLY:
        previousValue = isYearItem ? (standardValueExpenses * 12) : (standardValueExpenses * 3);
        break;
      case BmDataFillingType.MANUAL:
        if (isYearItem) {
          const quartersFG = BmCalcHelperService.findQuarterItemsInSelectedYear(formGroupExpenses.controls.items, item.value.period.yearNumber);
          previousValue = BmCalcHelperService.calculateSumOfQuarterValues(quartersFG);
        } else {
          previousValue = 0;
        }
        break;
    }
    return previousValue;
  }

  private updateLocalFormValue(isCopyEnabled: boolean): void {
    const newRawValue = this.budgetForm().getRawValue();
    if (isCopyEnabled) {
      mergeWith(this.budgetFormValue$$(), newRawValue);
    } else {
      this.budgetFormValue$$.set(newRawValue);
    }
    this.cdr.detectChanges();
  }
}
